import React, { FC, ReactNode, useMemo } from 'react'
import deepEqual from 'react-fast-compare'
import { Modal, Well } from '@pushly/aqe/lib/components'
import { NotificationDto } from '../../features/notifications'
import { INotificationBuilderState } from './interfaces/notification-builder-state.interface'
import { NotificationTestConfigurationModel } from '../../models/notification/notification-test-configuration.model'
import { NotificationActionModel } from '../../models/notification/notification-action.model'
import { isFloat, isIntValue } from '../../_utils/math'
import { DomainDto } from '../../dtos/domain'
import { NotificationSource } from '../../enums/notification-source'
import { StatusType } from '../../enums/status-type'
import { getDefaultNotificationActions } from '../../_utils/domain'
import { Browser, Platform } from '@pushly/cuttlefish'
import { IValidationResponse } from '../../interfaces/validation-response'
import { arrayContains, numberWithCommas, simpleNotification } from '../../_utils/utils'
import { Button, notification } from 'antd'
import { NotificationDeliveryType } from '../../enums/notification-delivery-type'
import { dateNow } from '../../_utils/datetime'
import { parseDateTimeInTz } from '../../_utils/moment'
import { PromiseableProp, resolvePromiseableProp } from '../../_utils/promiseable-prop'
import { NotificationVariantModel } from '../../models/notification/notification-variant.model'
import { NotificationExperienceModel } from '../../models/notification/notification-experience.model'
import {
    IosBadgeBehaviorAction,
    NdfTestScheduleType,
    NotificationBuilderLevel,
    NotificationBuilderSubmitType,
} from './enums'
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window'
import { stripUndefined } from '../../_utils/strip-undefined'
import { AppService, NotificationService } from '../../services'
import { Moment } from 'moment'
import {
    BASE_TIME_FORMAT,
    BASE_TIME_FORMAT_WITHOUT_TZ,
    FEAT_AUTO_KW_DISCOVERY,
    FEAT_IMM_NOTIF_CONFIRM,
    FEAT_NOTIF_DUPLICATE_CHECKS,
    FEAT_RESTRICT_ALL_SUB_NOTIFS,
    FEAT_SCHED_NOTIF_CONFIRM,
} from '../../constants'
import { SegmentDto } from '../../dtos/segment'
import moment from 'moment-timezone'
import { NotificationDeliveryModel } from '../../models/notification/notification-delivery.model'
import { AccountDto } from '../../dtos/account.dto'
import { AppState } from '../../stores/app'
import FlagList from '../../structs/flags-list'
import { AbilityAction } from '../../enums/ability-action.enum'
import { PreviewState } from './elements/notification-builder-preview'
import { BadgeBehaviorModel } from '../../models/notification/notification-native-ios-content.model'
import { NoTranslate } from '../no-translate/no-translate'
import { IBetterSelectOption } from '../better-select/interfaces'
import { NotificationTemplateService } from '../../services/notification-template'
import { GetNativeAndroidChannelResponse, WindowRange } from '@pushly/models/lib/structs'
import { DeliveryChannel, LandingUrlMatchMode, NotificationAbTestType, strIsKnownMacro } from '@pushly/models/lib/enums'
import { NotificationMatchQuery } from '@pushly/models/lib/structs/notif-conflicts-api/requests/notification-match-query'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { convertSeconds } from '@pushly/aqe/lib/utils'
import { WizardProvider } from '@pushly/aqe/lib/components/wizard/core/wizard-provider'
import { useWizard } from '@pushly/aqe/lib/components/wizard/core/use-wizard'
import { Wizard } from '@pushly/aqe/lib/components/wizard/wizard'
import { WizardStep } from '@pushly/aqe/lib/components/wizard/core/types'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { ActionDispatcher } from './types'

export const isAllFeatTest = (types?: string[]) => !!types && types.length === 2
export const isContentTest = (types?: string[]) =>
    isAllFeatTest(types) || (!!types && types.length === 1 && types[0] === 'content')
export const isDeliveryTest = (types?: string[]) =>
    isAllFeatTest(types) || (!!types && types.length === 1 && types[0] === 'delivery')
export const isSingleContent = (types?: string[]) => !isContentTest(types) && !isAllFeatTest(types)
export const isSingleDelivery = (types?: string[]) => !isDeliveryTest(types) && !isAllFeatTest(types)

export const getVariantTtlConfig = (
    ttlSource: DomainDto | AccountDto | undefined,
    model: NotificationExperienceModel,
) => {
    const globalDefaultTtlSeconds = 7 * 60 * 60 * 24
    const domainDefaultTtlSeconds = ttlSource?.defaultNotificationTtlSeconds
    const domainDefaultTtlMetric = ttlSource?.displayMeta?.default_notification_ttl_metric
    const ttlSeconds = model.getTtlSeconds() ?? domainDefaultTtlSeconds ?? globalDefaultTtlSeconds
    const ttlMetric = model.getTtlMetric() ?? domainDefaultTtlMetric ?? 'days'

    return { seconds: ttlSeconds, metric: ttlMetric }
}

type WellSubmitProps = Pick<
    Well['props'],
    'hideFooter' | 'disableSubmit' | 'onSubmit' | 'onCancel' | 'submitText' | 'cancelText' | 'submitActions'
>
type GetSubmitProps = {
    hide: boolean
    disabled: boolean
    submitText: string
    cancelText: string
    onSubmit: Well['props']['onSubmit']
    onCancel: Well['props']['onCancel']
    submitActions: Well['props']['submitActions']
}
export const getSubmitProps = ({
    hide,
    disabled,
    submitText,
    cancelText,
    submitActions,
    onSubmit,
    onCancel,
}: GetSubmitProps): WellSubmitProps => {
    return {
        hideFooter: hide,
        disableSubmit: disabled,
        onSubmit,
        onCancel,
        submitText,
        cancelText,
        submitActions,
    }
}

type ComputedSubmitTextProps = {
    formType: 'send' | 'named-template'
    formMode: 'create' | 'edit'
    variants: NotificationVariantModel[]
    override?: string
}
export const getComputedSubmitText = ({ formType, formMode, variants, override }: ComputedSubmitTextProps): string => {
    if (override) return override

    switch (formType) {
        case 'named-template': {
            return formMode === 'create' ? 'Create Template' : 'Update Template'
        }

        case 'send':
        default: {
            const deliveryTypes: NotificationDeliveryType[] = variants.map(
                (v) => v.getDelivery().getType() as NotificationDeliveryType,
            )

            const isMixed = deliveryTypes.length > 1
            const isImmediate = !isMixed && deliveryTypes[0] === NotificationDeliveryType.IMMEDIATE

            return isImmediate ? 'Send Now' : 'Schedule'
        }
    }
}

export const getComputedCancelText = (override?: string): string => {
    if (override) return override
    return 'Cancel'
}

export const parseLoadedVariant = (notif: NotificationDto): NotificationVariantModel => {
    const actions =
        notif.defaultTemplate.actions?.map(NotificationActionModel.build) ||
        notif.webTemplate.actions?.map(NotificationActionModel.build) ||
        notif.nativeAndroidTemplate.actions?.map(NotificationActionModel.build)

    const customActionsEnabled =
        notif.defaultTemplate.overrideDefaultActions ||
        notif.webTemplate?.displayMeta?.override_default_actions ||
        notif.nativeAndroidTemplate?.displayMeta?.override_default_actions

    return NotificationVariantModel.build({
        id: notif.id,
        audience: notif.audience,
        channels: notif.channels ?? notif.defaultTemplate.channels,
        content: {
            default: {
                title: notif.defaultTemplate.title,
                body: notif.defaultTemplate.body,
                keywords: notif.defaultTemplate.keywords,
                landingUrl: notif.defaultTemplate.landingUrl,
                altNativeUrl: notif.defaultTemplate.altNativeUrl,
                imageUrl: notif.defaultTemplate.imageUrl,
                iconUrl: notif.defaultTemplate.iconUrl,
                isUsingDomainDefaultIcon: notif.defaultTemplate.isUsingDomainDefaultIcon,
                badgeUrl: notif.defaultTemplate.badgeUrl,
                isUsingDomainDefaultBadge: notif.defaultTemplate.isUsingDomainDefaultBadge,
                actions,
                _custom_actions_enabled: customActionsEnabled,
                contentWebhookUrl: notif.defaultTemplate.contentWebhookUrl,
                overrideFallbackTemplate: notif.defaultTemplate.overrideFallbackTemplate,
                autoSuggestedFieldsResults: notif.defaultTemplate.autoSuggestedFieldsResults,
                experience_options: {
                    isSilent: notif.defaultTemplate.isSilent,
                    requireInteraction: notif.defaultTemplate.requireInteraction,
                    ttlSeconds: notif.deliverySpec.ttlSeconds,
                    ttlMetric: notif.deliverySpec.ttlMetric,
                },
                channels: notif.channels ?? notif.defaultTemplate.channels,
            },
            web: {
                title: notif.webTemplate.title,
                body: notif.webTemplate.body,
                keywords: notif.keywords,
                landingUrl: notif.webTemplate.landingUrl,
                imageUrl: notif.webTemplate.imageUrl,
                iconUrl: notif.webTemplate.iconUrl,
                actions: notif.webTemplate.actions?.map(NotificationActionModel.build),
                displayMeta: notif.webTemplate.displayMeta,
            },
            nativeIos: {
                title: notif.nativeIosTemplate.title,
                body: notif.nativeIosTemplate.body,
                imageUrl: notif.nativeIosTemplate.imageUrl,
                iconUrl: notif.nativeIosTemplate.iconUrl,
                landingUrl: notif.nativeIosTemplate.landingUrl,
                badgeBehavior: BadgeBehaviorModel.build(
                    typeof notif.nativeIosTemplate.badgeBehavior === 'string'
                        ? {
                              action: notif.nativeIosTemplate.badgeBehavior,
                              value: notif.nativeIosTemplate.badgeCount,
                          }
                        : notif.nativeIosTemplate.badgeBehavior,
                ),
                interruptionLevel: notif.nativeIosTemplate.interruptionLevel,
                relevanceScore: notif.nativeIosTemplate.relevanceScore,
                sound: notif.nativeIosTemplate.sound,
                subtitle: notif.nativeIosTemplate.subtitle,
                targetContentId: notif.nativeIosTemplate.targetContentId,
            },
            nativeAndroid: {
                title: notif.nativeAndroidTemplate.title,
                body: notif.nativeAndroidTemplate.body,
                imageUrl: notif.nativeAndroidTemplate.imageUrl,
                iconUrl: notif.nativeAndroidTemplate.iconUrl,
                landingUrl: notif.nativeAndroidTemplate.landingUrl,
                actions: notif.nativeAndroidTemplate.actions?.map(NotificationActionModel.build),
                displayMeta: notif.nativeAndroidTemplate.displayMeta,
                channelId: notif.nativeAndroidTemplate.channelId,
            },
        },
        delivery: {
            type: notif.deliverySpec.type,
            window: notif.deliverySpec.window,
            sendDateUtc: notif.deliverySpec.sendDateUtc,
            timezone: notif.deliverySpec.timezone,
        },
        weight: notif.abTest?.weight,
    })
}

export const getDefaultElapsedSchedule = (): any => {
    return {
        type: NdfTestScheduleType.ELAPSED,
        elapsed_seconds: 60 * 60,
        $elapsed_metric: 'hour',
    }
}

export const getDefaultSpecificSchedule = (delivery: NotificationDeliveryModel, domain: DomainDto): any => {
    let currSendDateObj: Moment
    if (delivery?.getSendDate()) {
        const currSendDateTz = delivery!.getTimeZone() ?? domain.timezone
        currSendDateObj = parseDateTimeInTz(delivery!.getSendDate(), currSendDateTz)
    } else {
        currSendDateObj = moment()
    }

    return {
        type: NdfTestScheduleType.SPECIFIC,
        evaluation_date_utc: currSendDateObj.clone().add(1, 'day').toISOString(),
        evaluation_date_tz: delivery.getTimeZone() ?? domain.timezone,
    }
}

export const resetVariantDistributionMap = (state: INotificationBuilderState) => {
    const isEditingTest = state.editNotificationType === 'test'
    if ((isEditingTest || state.variants.length > 1) && state.test) {
        const type = state.test.getDistributionType()?.toLowerCase() ?? 'split'
        const mapTotal = type === 'split' ? 100 : 24
        const map = getNormalizedDist(state.variants.length, mapTotal)
        state.test.setDistributionMap(map)
    }
}

export const addVariant = (v: NotificationVariantModel, state: INotificationBuilderState) => {
    const next = { ...state, variants: [...state.variants] }

    next.variants.push(v.clone(false, true))
    next.selectedVariantIdx = next.variants.length - 1

    if (!next.test) {
        next.test = NotificationTestConfigurationModel.build({})
    }

    resetVariantDistributionMap(next)

    return next
}

export const addMultipleVariants = (variants: NotificationVariantModel[], state: INotificationBuilderState) => {
    const next = { ...state, variants: [...state.variants] }

    // Add all variants without switching to any of them
    for (const variant of variants) {
        next.variants.push(variant.clone(false, true))
    }

    // Keep the current selected variant index (don't switch)
    // next.selectedVariantIdx stays the same

    if (!next.test) {
        next.test = NotificationTestConfigurationModel.build({})
    }

    resetVariantDistributionMap(next)

    return next
}

export const rmVariant = (idx: number, state: INotificationBuilderState) => {
    const next = { ...state, variants: [...state.variants] }

    const current = state.selectedVariantIdx
    const lastIndex = state.variants.length - 1

    let nextSelectedVariantIndex = current

    // when idx is the current selection
    if (idx === current) {
        // if idx is last: move selection to new last; otherwise keep current
        nextSelectedVariantIndex = current === lastIndex ? Math.max(0, lastIndex - 1) : current
    }
    // when idx is before the current selection: shift current left by 1
    else if (idx < current) {
        nextSelectedVariantIndex = current - 1
    }
    // when idx is after the current selection: keep current
    else {
        nextSelectedVariantIndex = current
    }

    next.variants.splice(idx, 1)
    next.selectedVariantIdx = nextSelectedVariantIndex

    const isEditingTest = state.editNotificationType === 'test'
    next.test = isEditingTest || next.variants.length > 1 ? state.test : undefined

    resetVariantDistributionMap(next)

    return next
}

export const removeAllOtherVariants = (state: INotificationBuilderState, variantIdxToKeep?: number) => {
    const selectedVariant = state.variants[variantIdxToKeep ?? state.selectedVariantIdx]!
    const isEditingTest = state.editNotificationType === 'test'

    const next = {
        ...state,
        variants: [selectedVariant],
        selectedVariantIdx: 0,
        test: isEditingTest ? state.test : undefined,
    }

    if (isEditingTest) {
        resetVariantDistributionMap(next)
    }

    return next
}

export const extractVariantPreview = (
    domain: DomainDto,
    variant: NotificationVariantModel,
    platform: PreviewPlatform,
    state: PreviewState,
    chromeless: boolean = false,
) => {
    const content = variant.getContent()?.getDefaultContent()
    const iosContent = variant.getContent()?.getNativeIosContent()

    const defaultTitle = 'Sample Notification Title'
    const defaultBadge = '/assets/logos/pushly_icon_2019.png'
    const actions = content.getActions()?.map((a) => a.serialize()) ?? getDefaultNotificationActions(domain)

    // intercept ecomm macro image
    let imageUrl = content.getImageUrl()
    if (imageUrl) {
        if (strIsKnownMacro(imageUrl, { exact: true })) {
            // set to transparent png
            imageUrl =
                'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+7G9EAAAAASUVORK5CYII='
        }
    }

    let iconUrl = content.getIconUrl()

    if ((platform === 'ios' || platform === 'android') && !iconUrl) {
        iconUrl = domain.defaultIconUrl
    }

    let badgeUrl = content.getBadgeUrl()
    if (content.getIsUsingDomainDefaultBadge() && !!domain.defaultBadgeUrl) {
        badgeUrl = badgeUrl ?? domain.defaultBadgeUrl
    }

    const body = content.getBody() ? <NoTranslate>{content.getBody()}</NoTranslate> : null
    const subtitle = iosContent.getSubtitle() ? <NoTranslate>{iosContent?.getSubtitle()}</NoTranslate> : null

    const nativeFcmConfig = domain.nativeFcmConfiguration as any

    const notificationShouldUseImageForIconWhenPresent = nativeFcmConfig.useImageForIconWhenPresent

    const previewConfig: any = {
        animated: false,
        browser: Browser.CHROME,
        platform,
        state,
        chromeless,
        appName: domain.displayName,
        notification: {
            title: content.getTitle() || defaultTitle,
            body,
            icon: iconUrl,
            useImageForIconWhenPresent: notificationShouldUseImageForIconWhenPresent,
            badgeColor: nativeFcmConfig.notificationAccentColor,
            image: imageUrl,
            badge: badgeUrl ?? defaultBadge,
            subtitle,
            actions:
                actions.length === 0
                    ? undefined
                    : actions
                          .sort((a, b) => (a.ordinal > b.ordinal ? 1 : -1))
                          .map((a) => ({
                              title: <NoTranslate>{a.label}</NoTranslate>,
                          })),
        },
    }

    if (!!domain) {
        previewConfig.notification.domain = domain.name
    }

    return previewConfig
}

export const validateTest = (builder: INotificationBuilderState): IValidationResponse => {
    let res: IValidationResponse = { ok: true, errors: [] }

    if (builder.test) {
        if (!builder.test.getName()?.trim()) {
            res.ok = false
            res.errors.push('Test name is required.')
        }

        if (builder.variants.length < 2) {
            res.ok = false
            res.errors.push('Tests must contain at least two variants.')
        }

        const distType = builder.test.getDistributionType()
        const evalSchedule = builder.test.getEvaluationSchedule()
        if (distType === 'sample' && evalSchedule && evalSchedule.getType() === NdfTestScheduleType.ELAPSED) {
            if (!isIntValue(evalSchedule.getElapsedSeconds())) {
                res.ok = false
                res.errors.push('Elapsed time after value is invalid.')
            }
        }
    }

    return res
}

export const validateAudience = (builder: INotificationBuilderState): IValidationResponse => {
    let res: IValidationResponse = { ok: true, errors: [] }

    const isOrgLevel = builder.level === NotificationBuilderLevel.ORG
    const isDomainLevel = builder.level === NotificationBuilderLevel.DOMAIN

    const variants = builder.variants
    const workingVariant = variants[builder.selectedVariantIdx]

    if (isOrgLevel) {
        const domainIds = workingVariant.getAudience().getDomainIds()
        const isAllSend = !domainIds && workingVariant.getAudience().getIsAll()

        if (!isAllSend && (!domainIds || domainIds.length === 0)) {
            res.ok = false
            res.errors.push('At least one audience domain is required.')
        }
    } else if (isDomainLevel) {
        const segmentIds = workingVariant.getAudience().getSegmentIds()
        const xSegmentIds = workingVariant.getAudience().getExcludedSegmentIds()

        if (!!segmentIds && segmentIds.length === 0) {
            res.ok = false
            res.errors.push('At least one audience segment is required.')
        }

        if (!!xSegmentIds && xSegmentIds.length === 0) {
            res.ok = false
            res.errors.push('At least one excluded audience segment is required, when exclusions are enabled.')
        }
    }

    return res
}

export const validateSelectedVariantContent = (builder: INotificationBuilderState): IValidationResponse => {
    let res: IValidationResponse = { ok: true, errors: [] }

    const isUsingTemplate = builder.usingTemplate
    const variants = builder.variants
    const workingVariant = variants[builder.selectedVariantIdx]
    const workingVariantContent = workingVariant.getContent().getDefaultContent()
    const landingUrl = workingVariantContent.getLandingUrl()?.trim()
    const title = workingVariantContent.getTitle()?.trim()
    const ttl = workingVariantContent.getExperienceOptions().getTtlSeconds()

    if (isUsingTemplate && !builder.selectedTemplate) {
        res.ok = false
        res.errors.push('A template must be selected if "Use saved template" is enabled.')
    }

    if (!landingUrl) {
        res.ok = false
        res.errors.push('A valid landing url is required.')
    }

    if (!title) {
        res.ok = false
        res.errors.push('Notification title is required.')
    }

    if (!ttl?.toString()?.trim()) {
        res.ok = false
        res.errors.push('Lifespan is required.')
    }

    return res
}

export const validateSelectedVariantDraftContent = (builder: INotificationBuilderState): IValidationResponse => {
    let res: IValidationResponse = { ok: true, errors: [] }

    const variants = builder.variants
    const workingVariant = variants[builder.selectedVariantIdx]
    const workingVariantContent = workingVariant.getContent().getDefaultContent()
    const title = workingVariantContent.getTitle()?.trim()

    if (!title) {
        res.ok = false
        res.errors.push('Notification title is required.')
    }

    return res
}

export const validateSelectedVariantDelivery = async (
    builder: INotificationBuilderState,
    domain: DomainDto,
    actionDispatcher?: ActionDispatcher,
): Promise<IValidationResponse> => {
    let res: IValidationResponse = { ok: true, errors: [] }

    const variants = builder.variants
    const workingVariant = variants[builder.selectedVariantIdx]
    const schedule = workingVariant.getDelivery()

    const type = schedule.getType()
    const isScheduled = type === NotificationDeliveryType.SCHEDULED
    const sendDate = schedule.getSendDate()

    if (isScheduled) {
        if (!sendDate?.toString()?.trim()) {
            res.ok = false
            res.errors.push('A valid delivery date/time is required.')
        } else {
            const now = dateNow().add(1, 'minute')
            const mmSendDate: Moment = parseDateTimeInTz(sendDate, schedule.getTimeZone()! ?? domain.timezone ?? 'utc')

            if (schedule.isUsingStzStrategy) {
                const stzSendDate: Moment = (mmSendDate as any).tz('utc', true).set({ second: 0, millisecond: 0 })
                const stzSendDateStr = stzSendDate.toISOString()

                if (builder.stzAvailabilityCache.has(stzSendDateStr)) {
                    res = builder.stzAvailabilityCache.get(stzSendDateStr)!
                } else {
                    actionDispatcher?.({
                        entity: 'stz-availability-validating',
                        type: 'set',
                        data: true,
                    })

                    const { data: validStz } = await NotificationService.fetchStzAvailability(stzSendDateStr)

                    if (!validStz) {
                        res.ok = false
                        res.errors.push('Requested delivery has passed in all subscriber time zones.')
                    }

                    actionDispatcher?.({
                        entity: 'stz-availability-cache',
                        type: 'set',
                        key: stzSendDateStr,
                        data: res,
                    })
                }
            } else if (mmSendDate <= now) {
                res.ok = false
                res.errors.push('Delivery date/time cannot be in the past.')
            }
        }
    }

    return res
}

export const renderValidationErrors = (validation: IValidationResponse) => {
    return validation.errors.map((err, idx) => (
        <span key={idx}>
            {err}
            <br />
        </span>
    ))
}

export const validateVariantSwitch = async (
    builder: INotificationBuilderState,
    domain: DomainDto,
    actionDispatcher: ActionDispatcher,
) => {
    notification.destroy()

    const contentValidation = validateSelectedVariantContent(builder)
    if (!contentValidation.ok) {
        simpleNotification(
            'error',
            <div>
                <p>
                    <b>Content</b>
                    <br />
                    {renderValidationErrors(contentValidation)}
                </p>
            </div>,
        )

        return false
    }

    const deliveryValidation = await validateSelectedVariantDelivery(builder, domain, actionDispatcher)
    if (!deliveryValidation.ok) {
        simpleNotification(
            'error',
            <div>
                <p>
                    <b>Delivery</b>
                    <br />
                    {renderValidationErrors(deliveryValidation)}
                </p>
            </div>,
        )

        return false
    }

    return true
}

export const validateSubmit = async (
    builder: INotificationBuilderState,
    domain: DomainDto,
    flags: FlagList,
    isPreviewSend?: boolean,
    isDraft?: boolean,
    appService?: AppService,
) => {
    notification.destroy()

    const isCampaignLevel = builder.level === NotificationBuilderLevel.CAMPAIGN

    const testValidation = validateTest(builder)
    const audienceValidation = validateAudience(builder)
    const contentValidation = validateSelectedVariantContent(builder)
    const deliveryValidation = await validateSelectedVariantDelivery(builder, domain)
    const distValidation = { ok: true, errors: [] }
    let valid =
        testValidation.ok && audienceValidation.ok && contentValidation.ok && deliveryValidation.ok && distValidation.ok

    if (!valid) {
        simpleNotification(
            'error',
            <div>
                {!testValidation.ok && (
                    <p>
                        <b>Test Configuration</b>
                        <br />
                        {renderValidationErrors(testValidation)}
                    </p>
                )}
                {!audienceValidation.ok && (
                    <p>
                        <b>Audience</b>
                        <br />
                        {renderValidationErrors(audienceValidation)}
                    </p>
                )}
                {!contentValidation.ok && (
                    <p>
                        <b>Content</b>
                        <br />
                        {renderValidationErrors(contentValidation)}
                    </p>
                )}
                {!deliveryValidation.ok && (
                    <p>
                        <b>Delivery</b>
                        <br />
                        {renderValidationErrors(deliveryValidation)}
                    </p>
                )}
                {!distValidation.ok && (
                    <p>
                        <b>Audience Distribution</b>
                        <br />
                        {renderValidationErrors(distValidation)}
                    </p>
                )}
            </div>,
        )
    }

    let confirmed = true
    if (valid && !isCampaignLevel && !isPreviewSend) {
        confirmed = await handleConfirmationRequirements(builder, domain, flags, isDraft, appService)
    }

    return valid && confirmed
}

export const validateSaveDraft = async (builder: INotificationBuilderState) => {
    notification.destroy()

    const contentValidation = validateSelectedVariantDraftContent(builder)
    const valid = contentValidation.ok

    if (!valid) {
        simpleNotification(
            'error',
            <div>
                {!contentValidation.ok && (
                    <p>
                        <b>Content</b>
                        <br />
                        {renderValidationErrors(contentValidation)}
                    </p>
                )}
            </div>,
        )
    }

    return valid
}

/**
 * Collects all notification state and domain state overlaps to determine if the user has confirm the send.
 * For each confirmation required, builds a confirmation wizard presented in a modal. Resolves true / false to determine if the send should proceed.
 *  - Cancelling confirm modal, returns false
 *  - When all steps are confirmed and final "Schedule" step is confirmed, return true
 *  - If no confirmation is required, returns true
 */
async function handleConfirmationRequirements(
    builder: INotificationBuilderState,
    domain: DomainDto,
    flags: FlagList,
    isDraft?: boolean,
    appService?: AppService,
): Promise<boolean> {
    // region definitions

    const confirmImmediateSendFlag = flags.findActive(FEAT_IMM_NOTIF_CONFIRM)?.getKey()
    const confirmScheduledSendFlag = flags.findActive(FEAT_SCHED_NOTIF_CONFIRM)?.getKey()
    const duplicateCheckFlag = flags.findActive(FEAT_NOTIF_DUPLICATE_CHECKS)?.getKey()

    const mustConfirmImmediateSend = !!confirmImmediateSendFlag && domain.flags.includes(confirmImmediateSendFlag)
    const mustConfirmScheduledSend = !!confirmScheduledSendFlag && domain.flags.includes(confirmScheduledSendFlag)
    const checkForDuplicateNotifications = !!duplicateCheckFlag && domain.flags.includes(duplicateCheckFlag)
    const checkForNotificationScheduleOverlap = domain.duplicateTimeWindow.enabled

    const deliveryInProgress = builder.variants.some((v) => v.getDisplayMeta()?.delivering ?? false)
    const builds = buildSubmittedNotificationPackages(domain, builder, flags)
    const shell = builds[0]!
    const audience = shell.audience

    const isOrgLevel = builder.level === NotificationBuilderLevel.ORG
    const isDomainLevel = builder.level === NotificationBuilderLevel.DOMAIN

    let isAllSend = false
    let iDomains: DomainDto[] = []
    let iSegments: SegmentDto[] = []
    let xSegments: SegmentDto[] = []

    const conflictQueries: NotificationMatchQuery[] = []
    const confirmationSteps: Array<WizardStep> = []
    const contentDuplicateQueryId = getRandId('nc_duplicate_query')
    const timeWindowQueryId = getRandId('nc_time_window_query')

    // for use in matching queries we always anchor our time window calculations to the sendDateUtc
    const now = new Date()
    const anchorDate = shell.deliverySpec.sendDateUtc ? new Date(shell.deliverySpec.sendDateUtc) : now
    const isAnchorFutureDated = anchorDate > now
    const timeWindowFriendlyDisplay = convertSeconds(
        domain.duplicateTimeWindow.intervalSeconds,
        domain.duplicateTimeWindow.displayMetric,
    )

    // endregion definitions

    // region build conflict queries

    if (checkForDuplicateNotifications) {
        if (audience) {
            if (isOrgLevel) {
                const variant = builder.variants[0]
                isAllSend = variant.getAudience().getIsAll()

                const ids = isAllSend ? builder.availableDomains.map((d) => d.id) : audience.domainIds ?? []
                iDomains = builder.availableDomains.filter((d) => arrayContains(ids, d.id))
                const audIds = variant.getAudience().getSegmentIds() ?? []
                iSegments = builder.availableSegments.filter((s) => arrayContains(audIds, s.id))

                if (iSegments.length === 1 && iSegments[0].isDefault) isAllSend = true
                // reset to all send when all domains are mnaually targeted
                if (!isAllSend && iDomains.length === builder.availableDomains.length) {
                    isAllSend = true
                }
            } else if (isDomainLevel) {
                if ('segmentIds' in audience) {
                    const ids = audience.segmentIds
                    iSegments = builder.availableSegments.filter((s) => arrayContains(ids, s.id))
                }

                // if all send segment has not been added to segmentIds, pull it from available
                if ('all_subscribers' in audience && audience.all_subscribers) {
                    isAllSend = true
                    iSegments = builder.availableSegments.filter((s) => s.isDefault)
                }

                if ('excludedSegmentIds' in audience) {
                    const ids = audience.excludedSegmentIds
                    xSegments = builder.availableSegments.filter((s) => arrayContains(ids, s.id))
                }
            }
        }

        conflictQueries.push(
            NotificationMatchQuery.from({
                query_id: contentDuplicateQueryId,
                landing_url: shell.template.channels.default.landingUrl,
                landing_url_match_mode: LandingUrlMatchMode.EXACT,
                segment_ids: iSegments.map((s) => s.id),
                status: [StatusType.COMPLETED.name, StatusType.DELIVERING.name, StatusType.SCHEDULED.name] as any,
                notification_id: builder.mode === 'create' ? undefined : builder.variants[0].getId(),
                window_range: WindowRange.from({
                    since: new Date(anchorDate.getTime() - 86400 * 7 * 1000).toISOString(), // 7 days ago
                    through: new Date(
                        anchorDate.getFullYear(),
                        anchorDate.getMonth(),
                        anchorDate.getDate(),
                        23,
                        59,
                        59,
                        999,
                    ).toISOString(), // end of anchor date
                }),
                limit: 1,
            }),
        )
    }

    if (checkForNotificationScheduleOverlap) {
        const duplicateTimeMs = domain.duplicateTimeWindow.intervalSeconds * 1000

        conflictQueries.push(
            NotificationMatchQuery.from({
                query_id: timeWindowQueryId,
                status: [StatusType.COMPLETED.name, StatusType.DELIVERING.name, StatusType.SCHEDULED.name] as any,
                notification_id: builder.mode === 'create' ? undefined : builder.variants[0].getId(),
                window_range: WindowRange.from({
                    since: new Date(anchorDate.getTime() - duplicateTimeMs).toISOString(),
                    through: new Date(anchorDate.getTime() + duplicateTimeMs).toISOString(),
                }),
                limit: 1,
            }),
        )
    }

    // endregion build conflict queries
    // region confirmation display components

    const WizardContent = ({ upper, middle, lower }: { upper: ReactNode; middle: ReactNode; lower: ReactNode }) => {
        return (
            <div className="notification-builder-wizard-content">
                <div className="notification-builder-wizard-content-upper">{upper}</div>
                <div className="notification-builder-wizard-content-middle">{middle}</div>
                <div className="notification-builder-wizard-content-lower">{lower}</div>
            </div>
        )
    }

    const NotificationSimpleListContentItem = ({
        title,
        id,
        date,
        timezone,
    }: {
        title: string
        id: string
        date: string
        timezone: string
    }) => {
        return (
            <>
                <a
                    href={appService?.routeWithin(isOrgLevel ? 'org' : 'domain', `/notifications/${id}/summary`, true)!}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    {title}&nbsp;{' '}
                </a>
                <span className="overlap-notification-subtext font-size--s">
                    ID: #{id}&nbsp; (
                    {new Date(date).toLocaleString(navigator.language ?? 'en-US', {
                        timeZone: domain.timezone ?? 'utc',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    })}
                    )
                </span>
            </>
        )
    }

    const SegmentSimpleListContentItem = ({ segment }: { segment: SegmentDto }) => {
        return (
            <>
                <a
                    href={
                        appService?.routeWithin(isOrgLevel ? 'org' : 'domain', `/segments/${segment.id}/summary`, true)!
                    }
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    {segment.name}&nbsp;
                </a>
                <span className="overlap-segment-subtext font-size--s"> ID: #{segment.id}</span>
            </>
        )
    }

    // endregion confirmation display components
    // region build confirmation steps from query results / definitions

    if (conflictQueries.length > 0) {
        const matchResponse = await NotificationService.executeNotificationDuplicateQueries(domain.id, conflictQueries)

        if (matchResponse.ok) {
            if (checkForDuplicateNotifications) {
                const match = matchResponse.data.results.find((r) => r.query_id === contentDuplicateQueryId)

                if (match?.matches?.length) {
                    const targetPhrase = isAnchorFutureDated
                        ? 'The selected audiences  have been targeted recently with the same landing URL within 7 days of the scheduled date.'
                        : 'The selected audiences are already targeted with the same landing URL within the last 7 days.'

                    confirmationSteps.push({
                        key: 'confirm-content-duplicate',
                        title: 'Audience Conflict',
                        description: 'Targeted Audience and Landing URL',
                        content: (
                            <WizardContent
                                upper={targetPhrase}
                                middle={
                                    <>
                                        <span>
                                            <b>Previous Notifications with the Same Audience and Landing URL</b>
                                        </span>
                                        <ul>
                                            {match.matches.map((notifMatch) => {
                                                return (
                                                    <li key={notifMatch.notification_id}>
                                                        <NotificationSimpleListContentItem
                                                            title={notifMatch.title}
                                                            id={notifMatch.notification_id}
                                                            date={notifMatch.scheduled_window.start}
                                                            timezone={domain.timezone}
                                                        />
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </>
                                }
                                lower="Please confirm that you would like to deliver this, possible, duplicate notification to the selected
                                    audiences."
                            />
                        ),
                    })
                }
            }

            if (checkForNotificationScheduleOverlap) {
                const match = matchResponse.data.results.find((r) => r.query_id === timeWindowQueryId)

                if (match?.matches?.length) {
                    confirmationSteps.push({
                        key: 'confirm-time-window',
                        title: 'Notification Schedule Overlap',
                        description: 'Notification Schedule',
                        content: (
                            <WizardContent
                                upper={`The following notification is already scheduled to be delivered within the same ${timeWindowFriendlyDisplay} ${domain.duplicateTimeWindow.displayMetric.slice(
                                    0,
                                    -1,
                                )} time window.`}
                                middle={
                                    <>
                                        <span>
                                            <b>Already Scheduled Notifications</b>
                                        </span>
                                        <br />
                                        <ul>
                                            {match.matches.map((notifMatch) => {
                                                return (
                                                    <li key={notifMatch.notification_id}>
                                                        <NotificationSimpleListContentItem
                                                            title={notifMatch.title}
                                                            id={notifMatch.notification_id}
                                                            date={notifMatch.scheduled_window.start}
                                                            timezone={domain.timezone}
                                                        />
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </>
                                }
                                lower="Please confirm that you would like to deliver this notification in the same time window."
                            />
                        ),
                    })
                }
            }
        }
    }

    const isSTZ = shell.deliverySpec.window === NotificationDeliveryWindow.TIMEZONE
    const draftRemovalText = 'This notification will no longer be shown in the Notification Drafts tab.'

    if (isSTZ && deliveryInProgress) {
        confirmationSteps.push({
            key: 'confirm-stz-update',
            title: 'Confirm Delivery Update',
            description: 'Subscriber Time Zone Changes',
            content: (
                <div>
                    Changes made to this notification will take effect for subscribers in time zones that have not yet
                    began delivery. Please confirm that you would like to make these changes?
                </div>
            ),
        })
    } else if (
        shell.deliverySpec.type === NotificationDeliveryType.IMMEDIATE &&
        (mustConfirmImmediateSend || isAllSend)
    ) {
        confirmationSteps.push({
            key: 'confirm-immediate-send',
            title: 'Confirm Immediate Send',
            description: 'Immediate Send',
            content: (
                <WizardContent
                    upper={
                        <span>
                            Please confirm that you would like to send this notification <b>immediately</b> to the
                            following audiences.
                        </span>
                    }
                    middle={
                        <>
                            <span>
                                <b>Targeted Audiences</b>
                            </span>
                            <ul>
                                <li>
                                    {iSegments.map((s) => {
                                        return (
                                            <div key={s.id}>
                                                <SegmentSimpleListContentItem segment={s} />
                                            </div>
                                        )
                                    })}
                                </li>
                            </ul>
                        </>
                    }
                    lower={isDraft ? <div>{draftRemovalText}</div> : null}
                />
            ),
        })
    } else if (
        shell.deliverySpec.type === NotificationDeliveryType.SCHEDULED &&
        (mustConfirmScheduledSend || isAllSend)
    ) {
        const scheduledDateTime = moment
            .tz(shell.deliverySpec.sendDateUtc, shell.deliverySpec.timezone)
            .format(isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT)

        confirmationSteps.push({
            key: 'confirm-scheduled-send',
            title: 'Confirm Scheduled Send',
            description: 'Scheduled Send',
            content: (
                <WizardContent
                    upper={
                        <span>
                            Please confirm that you would like to schedule this notification to be sent to{' '}
                            {numberWithCommas(builder.reachEstimate || 0)} subscribers
                            {isOrgLevel ? '?' : ` at ${scheduledDateTime}.`}
                        </span>
                    }
                    middle={
                        <>
                            <span>
                                <b>Targeted Audiences</b>
                            </span>
                            <ul>
                                <li>
                                    {iSegments.map((s) => {
                                        return (
                                            <div key={s.id}>
                                                <SegmentSimpleListContentItem segment={s} />
                                            </div>
                                        )
                                    })}
                                </li>
                            </ul>
                        </>
                    }
                    lower={isDraft ? <div>{draftRemovalText}</div> : null}
                />
            ),
        })
    }

    // endregion build confirmation steps from query results / definitions
    // region Show Confirmation Modal

    if (confirmationSteps.length > 0) {
        return new Promise((res) => {
            const ConfirmationWizardContent: FC<{ close: (ok: boolean) => void }> = ({ close }) => {
                const { reset, cancel, complete, next, previous, isLast, isFirst } = useWizard()

                return (
                    <div>
                        <Wizard steps={confirmationSteps} hideActions={true} />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                            <Button
                                onClick={() => {
                                    reset()
                                    cancel()
                                    close(false)
                                }}
                                type="primary"
                                danger={true}
                            >
                                Cancel
                            </Button>
                            {!isFirst && (
                                <Button
                                    type="text"
                                    onClick={() => {
                                        previous()
                                    }}
                                >
                                    Previous
                                </Button>
                            )}
                            <Button
                                type="primary"
                                onClick={() => {
                                    if (isLast) {
                                        complete()
                                        close(true)
                                    } else {
                                        next()
                                    }
                                }}
                            >
                                {isLast ? 'Confirm & Schedule' : 'Confirm & Continue'}
                            </Button>
                        </div>
                    </div>
                )
            }

            let instance

            const closeModal = (ok: boolean) => {
                try {
                    instance?.destroy?.()
                } finally {
                    res(ok)
                }
            }

            instance = Modal.confirm({
                title: 'Delivery Confirmations',
                className: 'notification-builder-confirm-modal',
                content: (
                    <WizardProvider steps={confirmationSteps} allowArbitraryNavigation={false}>
                        <ConfirmationWizardContent close={closeModal} />
                    </WizardProvider>
                ),
                onOk: () => false,
                onCancel: () => closeModal(false),
            })
        })
    }

    // endregion Show Confirmation Modal

    return true
}

export const getNormalizedDist = (parts: number, total: number = 100): number[] => {
    const basePct = total / parts
    const rawDist = new Array(parts).fill(Math.floor(basePct))
    if (isFloat(basePct)) {
        rawDist[rawDist.length - 1] = Math.ceil(basePct)
    }

    return Array.from(
        rawDist.sort((a, b) => a - b),
        (_, i) => i,
    )
        .sort((a, b) => (b % 2) - (a % 2) || (a % 2 ? a - b : b - a))
        .map((i) => rawDist[i])
}

export const resolveNotificationsProp = async (
    domain: DomainDto,
    notifications: PromiseableProp<NotificationDto[] | undefined>,
    builder: INotificationBuilderState,
    defaultSelectedId?: number,
): Promise<Partial<INotificationBuilderState>> => {
    const initialUpdate: Partial<INotificationBuilderState> = {
        initialNotificationStateLoaded: true,
        test: builder.test,
        variants: builder.variants,
    }

    if (!!notifications) {
        let loadedNotifications = await resolvePromiseableProp(notifications)
        // Ensure no null|undefined values
        loadedNotifications = loadedNotifications?.filter((n) => !!n)

        initialUpdate.initialNotificationState = loadedNotifications

        if (Array.isArray(loadedNotifications) && loadedNotifications.length > 0) {
            initialUpdate.variants = []

            loadedNotifications.forEach((notif, idx) => {
                if (!initialUpdate.test && notif.abTest?.id) {
                    initialUpdate.test = NotificationTestConfigurationModel.build(notif.abTest)
                }

                const variant = parseLoadedVariant(notif)
                if (variant.getChannels()) {
                    initialUpdate.channels = variant.getChannels()
                }
                const content = variant.getContent()

                if (notif.defaultTemplate.overrideDefaultActions === false) {
                    content.getDefaultContent().setActions(undefined)
                }

                /**
                 * Undefined media initialization edge-cases
                 *
                 * campaign node builders are dumb by design
                 *  and not domain aware - domain backfill logic
                 *  is relegated to the notification load/parsing
                 *  utilities.
                 */

                // ensure icon settings are properly initialized
                // cannot test against null as it is a valid value
                if (
                    notif.defaultTemplate.isUsingDomainDefaultIcon === undefined &&
                    notif.defaultTemplate.iconUrl === undefined
                ) {
                    // if campaign audience default icon url is set, it is a campaign segment override
                    if (builder.campaign?.configuration?.audience?.default_icon_url) {
                        content.getDefaultContent().setIconUrl(builder.campaign.configuration.audience.default_icon_url)
                    } else if (domain.defaultIconUrl) {
                        content.getDefaultContent().setIsUsingDomainDefaultIcon(true)
                        content.getDefaultContent().setIconUrl(domain.defaultIconUrl)
                    }
                }

                // ensure badge settings are properly initialized
                // cannot test against null as it is a valid value
                if (
                    notif.defaultTemplate.isUsingDomainDefaultBadge === undefined &&
                    notif.defaultTemplate.badgeUrl === undefined &&
                    !!domain.defaultBadgeUrl
                ) {
                    content.getDefaultContent().setIsUsingDomainDefaultBadge(true)
                    content.getDefaultContent().setBadgeUrl(domain.defaultBadgeUrl)
                }

                // Parse loaded ttl settings
                const expOpts = variant.getContent().getDefaultContent().getExperienceOptions()
                const ttlSet = getVariantTtlConfig(domain, expOpts)
                expOpts.setTtlSeconds(ttlSet.seconds)
                expOpts.setTtlMetric(ttlSet.metric)

                initialUpdate.variants!.push(variant)

                if (notif.id?.toString() === defaultSelectedId?.toString()) {
                    initialUpdate.selectedVariantIdx = idx
                }

                // set editable state
                if (builder.mode === 'edit') {
                    const isImmFixed =
                        notif.deliverySpec.type === NotificationDeliveryType.IMMEDIATE &&
                        notif.deliverySpec.window === NotificationDeliveryWindow.STANDARD
                    const isDraft = notif.status === StatusType.DRAFT.name
                    const isStarted = notif.status === StatusType.DELIVERING.name
                    const isCompleted =
                        notif.status === StatusType.COMPLETED.name ||
                        notif.status === StatusType.COMPLETED_WITH_FAILURES.name
                    const isFailed = notif.status === StatusType.FAILED.name
                    const isCancelled =
                        notif.status === StatusType.CANCELLED.name || notif.status === StatusType.CANCELLING.name

                    const editable = !isFailed && !isCancelled && !isCompleted && !(isImmFixed && isStarted)

                    variant.setDisplayMeta({
                        notEditable: !editable,
                        delivering: isStarted,
                        isDraft,
                        sourceTemplateId: notif.template.sourceTemplateId ?? null,
                    })

                    if (builder.type === 'template') {
                        initialUpdate.details = {
                            name: notif.template.name,
                            description: notif.template.description,
                        }
                    }
                } else {
                    const isTemplate = builder.type === 'template'
                    if (isTemplate) {
                        variant.setDisplayMeta({ isTemplate })
                    }
                }
            })

            if (initialUpdate.test) {
                const test = initialUpdate.test

                // Update loaded distribution map
                test.setDistributionMap(initialUpdate.variants.map((v) => v.getWeight()))

                // Handle legacy Content & Delivery
                const deliverySpecs = initialUpdate.variants.map((v) => v.getDelivery().serialize())
                const allSpecsMatch = deliverySpecs.every((v, i, arr) => deepEqual(v, arr[0]))
                const isLegacyAllType =
                    !loadedNotifications[0].abTest?.testTypes && isSingleDelivery(test.getTestTypes()) && !allSpecsMatch
                if (isLegacyAllType) {
                    test.setTestTypes([NotificationAbTestType.CONTENT, NotificationAbTestType.DELIVERY])
                }
            }

            if (builder.mode === 'edit') {
                initialUpdate.editNotificationType = initialUpdate.test ? 'test' : 'single'
            }
        }
    }

    return initialUpdate
}

export const resolveTemplatesProp = async (
    domain: DomainDto,
    builder: INotificationBuilderState,
    defaultSelectedTemplateId: number | undefined,
) => {
    const initialUpdate: Partial<INotificationBuilderState> = {
        initialTemplatesStateLoaded: true,
        availableTemplates: [],
        selectedTemplate: defaultSelectedTemplateId,
        usingTemplate: defaultSelectedTemplateId !== undefined,
    }

    const templateService = new NotificationTemplateService()
    const { data: templates } = await templateService.fetchNamedNotificationTemplatesByDomainId(domain.id, {
        query: { include_actions: true, pagination: 0 },
    })
    initialUpdate.availableTemplates = templates

    if (initialUpdate.selectedTemplate) {
        const currTemplate = templates.find((t) => t.id === defaultSelectedTemplateId)
        initialUpdate.details = {
            name: currTemplate?.name,
            description: currTemplate?.description,
        }
    }

    return initialUpdate
}

export function getNextVariantWithDefaultNativeAndroidChannel(
    builder: INotificationBuilderState,
    nativeAndroidChannels: GetNativeAndroidChannelResponse[],
) {
    const currVariant = builder.variants[builder.selectedVariantIdx]
    const nativeAndroidContent = currVariant.getContent().getNativeAndroidContent()

    // once loaded if the android content does not have a channel set AND the domain has a default native android channel we should set it
    if (!nativeAndroidContent.getChannelId()) {
        const defaultNativeAndroidChannel = nativeAndroidChannels.find((ch) => ch.is_default)
        if (defaultNativeAndroidChannel) {
            nativeAndroidContent.setChannelId(defaultNativeAndroidChannel.id)

            currVariant.getContent().setNativeAndroidContent(nativeAndroidContent)
        }
    }

    return currVariant
}

export const canCreateSendToAll = (domain: DomainDto, appState: AppState): Boolean => {
    if (!domain) {
        return false
    }

    const domainFlags = [...(domain.flags ?? []), ...(domain.accountFlags ?? [])]
    const hasRestrictAllSubsFlag = domainFlags.includes(FEAT_RESTRICT_ALL_SUB_NOTIFS)
    const userIsAdmin = appState.abilityStore.can(AbilityAction.UPDATE, appState.currentDomain!)

    return userIsAdmin || !hasRestrictAllSubsFlag
}

export const usePreviewOptions = (
    channels: DeliveryChannel[],
    opts: {
        defaultValue?: IBetterSelectOption[]
    } = {},
): IBetterSelectOption[] => {
    return useMemo(() => {
        return buildPreviewOptions(channels) ?? opts.defaultValue
    }, [channels, opts.defaultValue])
}

export const buildPreviewOptions = (channels: DeliveryChannel[]): IBetterSelectOption[] => {
    const optSet = new Set<IBetterSelectOption>([])

    for (const channel of channels) {
        if (channel === DeliveryChannel.WEB) {
            optSet.add({
                value: Platform.ANDROID,
                label: 'Android (Web)',
            })

            optSet.add({
                value: Platform.WINDOWS,
                label: 'Web / Windows',
            })

            optSet.add({
                value: Platform.MAC_OS,
                label: 'MacOS',
            })
        } else if (channel === DeliveryChannel.NATIVE_ANDROID) {
            optSet.add({
                value: Platform.NATIVE_ANDROID,
                label: 'Android (Native)',
            })
        } else if (channel === DeliveryChannel.NATIVE_IOS) {
            optSet.add({
                value: Platform.IOS,
                label: 'Apple / iOS',
            })
        }
    }

    return [...optSet.values()].sort((a, b) => (a.label! > b.label! ? -1 : 1))
}

export const buildSubmittedNotificationPackages = (
    domain: DomainDto,
    builder: INotificationBuilderState,
    flags: FlagList,
    type: NotificationBuilderSubmitType = NotificationBuilderSubmitType.STANDARD,
    additionalMeta?: any,
    initialCreate?: boolean,
): any[] => {
    const builds: any[] = []
    const test = builder.test
    const isPreviewSend = type !== NotificationBuilderSubmitType.STANDARD
    const autoKwFlag = flags.findActive(FEAT_AUTO_KW_DISCOVERY)?.getKey()
    const hasAutoKwDiscoveryFlag = autoKwFlag && domain.flags?.includes(autoKwFlag)

    const isOrgLevel = builder.level === NotificationBuilderLevel.ORG
    const isDomainLevel = builder.level === NotificationBuilderLevel.DOMAIN
    const previousHasNativeAndroidChannel =
        builder.mode === 'edit' &&
        builder.initialNotificationState.some((notif) => notif.channels.includes(DeliveryChannel.NATIVE_ANDROID))
    const previousHasNativeIosChannel =
        builder.mode === 'edit' &&
        builder.initialNotificationState.some((notif) => notif.channels.includes(DeliveryChannel.NATIVE_IOS))
    const previousHasWebChannel =
        builder.mode === 'edit' &&
        builder.initialNotificationState.some((notif) => notif.channels.includes(DeliveryChannel.WEB))

    builder.variants.forEach((v, vidx) => {
        const build: any = {
            source:
                builder.level === NotificationBuilderLevel.CAMPAIGN
                    ? NotificationSource.CAMPAIGN
                    : NotificationSource.MANUAL,
            meta: {
                origin_ui: 'new-notif-flow',
                ...(additionalMeta ?? {}),
            },
        }

        if (isDomainLevel) {
            build.domainId = domain.id
        }

        if (isPreviewSend) {
            build.source = NotificationSource.PREVIEW
            build.meta.origin_domain_id = domain.id
            build.meta.preview_type =
                type === NotificationBuilderSubmitType.PERSONAL_PREVIEW ? 'personal-preview' : 'team-preview'
        } else if (/^[0-9]+$/.test(v.getId())) {
            build.id = v.getId()
        }

        const audience = v.getAudience()
        const content = v.getContent()
        const defaultContent = content.getDefaultContent()
        const webContent = content.getWebContent()
        const nativeIosContent = content.getNativeIosContent()
        const nativeAndroidContent = content.getNativeAndroidContent()
        const exp = content.getDefaultContent().getExperienceOptions()
        const delivery = v.getDelivery()

        if (isOrgLevel && (audience.getIsAll() || (audience.getDomainIds()?.length ?? 0) > 1)) {
            build.meta.is_multi_domain = true
        }

        build.channels = builder.channels

        if (!isPreviewSend && test) {
            build.abTest = {
                id: test.getId(),
                name: test.getName(),
                testTypes: test.getTestTypes(),
                deliveryType: test.getDistributionType(), // API expects deliveryType - UI uses DistribtuionType
                ctrThreshold: test.getCtrThreshold() ?? null,
                weight: test.getDistributionMap()?.[vidx],
            }

            if (test.getDistributionType() === 'sample' && !!test.getEvaluationSchedule()) {
                const schedule = test.getEvaluationSchedule()!
                const scheduleBuild: any = {
                    type: schedule.getType(),
                }

                if (schedule.getType() === NdfTestScheduleType.ELAPSED) {
                    scheduleBuild.$elapsed_metric = schedule.getElapsedMetric() ?? 'hour'
                    scheduleBuild.elapsed_seconds = schedule.getElapsedSeconds()
                } else {
                    scheduleBuild.$evaluation_date_timezone = schedule.getEvaluationDateTimeZone() ?? domain.timezone
                    scheduleBuild.evaluation_date_utc = schedule.getEvaluationDateUtc()
                }

                build.abTest.evaluationSchedule = scheduleBuild
            }
        }

        // Audience
        if (type === NotificationBuilderSubmitType.PERSONAL_PREVIEW) {
            build.audience = { personalPreview: true }
        } else if (type === NotificationBuilderSubmitType.TEAM_PREVIEW) {
            build.audience = { teamPreview: true }
        } else if (isOrgLevel) {
            build.audience = {
                domainIds: audience.getIsAll() ? builder.availableDomains.map((d) => d.id) : audience.getDomainIds(),
            }

            if (audience.getSegmentIds()?.length) {
                build.audience.segmentIds = audience.getSegmentIds()
            }
        } else {
            if (audience.getIsAll()) {
                build.audience = {
                    all_subscribers: true,
                }
            } else {
                build.audience = {
                    segmentIds: audience.getSegmentIds(),
                    excludedSegmentIds: audience.getExcludedSegmentIds(),
                }
            }
        }

        // Delivery
        if (builder.type === 'notification') {
            if (isPreviewSend) {
                build.deliverySpec = {
                    type: NotificationDeliveryType.IMMEDIATE,
                    window: NotificationDeliveryWindow.STANDARD,
                }
            } else {
                build.deliverySpec = {
                    type: delivery.getType(),
                    window: delivery.getWindow(),
                    sendDateUtc: delivery.getSendDate(),
                    timezone: delivery.getTimeZone(),
                    ttlSeconds: exp.getTtlSeconds(),
                    ttlMetric: exp.getTtlMetric(),
                }
            }
        }

        let actions: any[] | undefined = defaultContent.getActions()?.map((action) => ({
            // model validation now expects string as data id is BigInt / string - update when model is fixed
            id: action?.getId()?.toString(),
            ordinal: action.getOrdinal(),
            type: action.getType(),
            label: action.getLabel(),
            landingUrl: action.getLandingUrl(),
            usePrimaryLandingUrl: action.getUsePrimaryLandingUrl(),
        }))

        if (defaultContent.getCustomActionsEnabled() && (actions?.length ?? 0) === 0) {
            actions = getDefaultNotificationActions(domain)
        }

        // build default template
        const template: any = {
            channels: {},
        }

        template.channels.default = {
            channels: build.channels,
            title: defaultContent.getTitle(),
            body: defaultContent.getBody(),
            landingUrl: defaultContent.getLandingUrl() === '' && initialCreate ? null : defaultContent.getLandingUrl(),
            imageUrl: defaultContent.getImageUrl(),
            iconUrl: defaultContent.getIconUrl(),
            contentWebhookUrl: defaultContent.getContentWebhookUrl(),
            overrideFallbackTemplate: defaultContent.getOverrideFallbackTemplate(),
            badgeUrl: defaultContent.getBadgeUrl(),
            isUsingDomainDefaultIcon: defaultContent.getIsUsingDomainDefaultIcon(),
            isUsingDomainDefaultBadge: defaultContent.getIsUsingDomainDefaultBadge(),
            overrideDefaultActions: defaultContent.getCustomActionsEnabled(),
            autoSuggestedFieldsResults: defaultContent.getAutoSuggestedFieldsResults(),
            actions: defaultContent.getCustomActionsEnabled() ? actions : undefined,
            isSilent: exp.getIsSilent(),
            requireInteraction: exp.getRequireInteraction(),
        }

        // build ios content
        if (build.channels.includes(DeliveryChannel.NATIVE_IOS)) {
            template.channels.nativeIos = {
                landingUrl: nativeIosContent.getLandingUrl() ?? null,
                category: nativeIosContent.getCategory() ?? null,
                subtitle: nativeIosContent.getSubtitle() ?? null,
                sound: nativeIosContent.getSound() ?? null,
                interruptionLevel: nativeIosContent.getInterruptionLevel() ?? null,
                relevanceScore: nativeIosContent.getRelevance() ?? null,
                targetContentId: nativeIosContent.getTargetContentId() ?? null,
                title: nativeIosContent.getTitle() ?? null,
                body: nativeIosContent.getBody() ?? null,
                iconUrl: nativeIosContent.getIconUrl() ?? null,
                imageUrl: nativeIosContent.getImageUrl() ?? null,
            }

            const badgeBehavior = nativeIosContent.getBadgeBehavior() ?? null
            if (!badgeBehavior || badgeBehavior.getAction() === IosBadgeBehaviorAction.DO_NOTHING) {
                template.channels.nativeIos.badgeBehavior = null
            } else {
                template.channels.nativeIos.badgeBehavior = badgeBehavior
            }
        } else if (previousHasNativeIosChannel) {
            template.channels.nativeIos = null
        }

        // build native android content
        if (build.channels.includes(DeliveryChannel.NATIVE_ANDROID)) {
            template.channels.nativeAndroid = {
                landingUrl: nativeAndroidContent.getLandingUrl() ?? null,
                title: nativeAndroidContent.getTitle() ?? null,
                body: nativeAndroidContent.getBody() ?? null,
                iconUrl: nativeAndroidContent.getIconUrl() ?? null,
                imageUrl: nativeAndroidContent.getImageUrl() ?? null,
                channelId: nativeAndroidContent.getChannelId() ?? null,
                // actions from UI are built via default -- eventually will decouple
                actions: defaultContent.getCustomActionsEnabled() ? actions : undefined,
                displayMeta: {
                    override_default_actions: defaultContent.getCustomActionsEnabled(),
                },
            }
        } else if (previousHasNativeAndroidChannel) {
            template.channels.nativeAndroid = null
        }

        // build web content
        if (build.channels.includes(DeliveryChannel.WEB)) {
            template.channels.web = {
                // currently no separation between default and current
                landingUrl: defaultContent.getLandingUrl() || null,
                title: webContent.getTitle() || null,
                body: webContent.getBody()?.trim() || null,
                imageUrl: webContent.getImageUrl()?.trim() ?? null,
                iconUrl: webContent.getIconUrl()?.trim() ?? null,
                // actions from UI are built via default -- eventually will decouple
                actions: defaultContent.getCustomActionsEnabled() ? actions : undefined,
                displayMeta: {
                    override_default_actions: defaultContent.getCustomActionsEnabled(),
                },
            }
        } else if (previousHasWebChannel) {
            template.channels.web = null
        }

        build.template = {
            channels: template.channels,
            keywords: defaultContent.getKeywords(),
            auto_keyword_discovery: hasAutoKwDiscoveryFlag ? true : undefined,
        }

        if (builder.selectedTemplate) {
            build.template.source_template_id = builder.selectedTemplate
        }

        if (builder.type === 'template') {
            build.name = builder.details.name
            build.description = builder.details.description === '' ? null : builder.details.description

            build.ttlSpec = {
                ttlSeconds: exp.getTtlSeconds(),
                ttlMetric: exp.getTtlMetric(),
            }
        }

        builds.push(stripUndefined(build))
    })

    return builds
}

/**
 * Builder function to get the initial delivery config for a variant.
 *
 * @param level - The current builder level
 * @param domain - the active domain
 *
 * @returns NotificationDeliveryModel
 */
type InitialNotificationDeliveryBuilder = (
    level: NotificationBuilderLevel,
    domain: DomainDto,
) => NotificationDeliveryModel

/**
 * Normalize initial builder test config and variant deliveries to ensure no legacy configurations.
 *
 * Resets any {@link NotificationAbTestType.DELIVERY} test types to {@link NotificationAbTestType.CONTENT}
 * and removes any {@link NotificationDeliveryWindow.HOLD_OUT} delivery strategies.
 *
 * @param mode - The current builder mode ('edit' or 'create')
 * @param level - The current builder level
 * @param domain - The active domain
 * @param test - The current builder test config
 * @param variants - The current builder variants
 * @param buildInitialDeliveryModel - Builder function to get the initial delivery config for a variant
 *
 * @mutating
 */
export function normalizeNotificationDeliveries(
    mode: 'edit' | 'create',
    level: NotificationBuilderLevel,
    domain: DomainDto,
    test: NotificationTestConfigurationModel | undefined,
    variants: NotificationVariantModel[] | undefined,
    buildInitialDeliveryModel: InitialNotificationDeliveryBuilder,
): void {
    // Normalization only applies to domain level create mode
    if (mode !== 'create' || level !== NotificationBuilderLevel.DOMAIN || !variants?.length) {
        return
    }

    const defaultDelivery = buildInitialDeliveryModel(level, domain)

    // If the test types include DELIVERY (no longer supported), we need to update
    // the test type to CONTENT only and evaluate current delivery configurations.
    if (test?.getTestTypes().includes(NotificationAbTestType.DELIVERY)) {
        test.setTestTypes([NotificationAbTestType.CONTENT])

        const nonHoldOutDelivery = variants.filter((v) => !v.getDelivery().isUsingHoldOutStrategy)
        if (nonHoldOutDelivery?.length) {
            // When a non-HOLD_OUT variant is found, update all variants to use its delivery settings.
            const now: Moment = dateNow(domain)
            const safeNow: Moment = moment(now).add(5, 'minutes')

            const deliverySample = nonHoldOutDelivery[0].getDelivery().clone()
            const sampleIsScheduled = deliverySample.getType() === NotificationDeliveryType.SCHEDULED
            const sampleSendDateStr = deliverySample.getSendDate()
            const sampleSendDate: Moment = sampleSendDateStr ? moment(sampleSendDateStr) : safeNow

            // Ensure the start date is not in the past.
            if (sampleIsScheduled && sampleSendDate < now) {
                deliverySample.setSendDate(safeNow.toISOString())
            }

            // Update all variants to use the same delivery strategy.
            variants.forEach((v) => v.setDelivery(deliverySample.clone()))
        } else {
            // This scenario (only HOLD_OUT variants) should never be reachable due to business logic.
            // In the event it is reached, reset all delivery settings to the default value.
            variants.forEach((v) => v.setDelivery(defaultDelivery.clone()))
        }

        return
    }

    // If it is a single notification and HOLD_OUT, update to the default delivery for a new notification.
    if (variants[0].getDelivery().isUsingHoldOutStrategy) {
        variants[0].setDelivery(defaultDelivery.clone())
    }
}
